import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FilterMatchMode } from 'primereact/api';
import { addLocale, locale } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

import { ScheduleService } from 'components/services/ScheduleService';
import SearchInputWithClear from 'ui/custom-controls/SearchInputWithClear';
import ScheduleCard from 'ui/ScheduleCard';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import TablePageHeader from 'ui/shared/TablePageHeader';

import { ScheduleTemplate } from '../constants/ScheduleTemplate';
import { isSelected } from '../helpers';
import { useDebounce } from '../hooks';
import { ruLocale } from '../lang/ruLocale';
import { Schedule, ScheduleWithSettings } from '../types/interfaces';
import pageSizeValues from '../ui/shared/paginator/pageSizeValues';
import { clearFilter } from '../utils';
import VisibilityWrapper from '../utils/wrappers/VisibilityWrapper';

export default function Schedules() {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<
    string | number | null
  >(null);
  const [selectedSchedule, setSelectedSchedule] =
    useState<ScheduleWithSettings>(ScheduleTemplate);
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS },
    value: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  const [loading, setLoading] = useState<boolean>(true);
  const nameFilterRef = useRef<HTMLInputElement>(null);
  const descriptionFilterRef = useRef<HTMLInputElement>(null);

  const globalFilterRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);
  const scheduleCardRef = useRef<null | {
    handleCancel: () => void;
  }>(null);

  const isSelectedIdExist = isSelected(selectedSchedule);
  const [pageSize, setPageSize] = useState(
    pageSizeValues[pageSizeValues.length - 1].value
  );
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const [first, setFirst] = useState(0);
  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);
  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setLoading(true);
    setMultiSortMeta(e.multiSortMeta);
  }, []);
  const onPageChanged = useCallback(async (event: DataTablePageEvent) => {
    if (event.rows !== undefined) {
      setFirst(0);
      setPageSize(event.rows);
    }

    if (event.page !== undefined) {
      setFirst(event.first);
      setPageNumber(event.first / event.rows + 1);
    }
  }, []);

  addLocale('ru', ruLocale.ru);
  locale('ru');

  const filtersForApi = useMemo(() => {
    const sort = multiSortMeta?.find((x) => x !== undefined);
    const baseFilters = Object.keys(filters).map((x) => {
      const f = filters[x];
      return {
        field: x,
        value: (f as DataTableFilterMetaData).value,
        operator: (f as DataTableFilterMetaData).matchMode
      };
    });

    return {
      pageNumber,
      pageSize,
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: JSON.stringify(baseFilters)
    };
  }, [filters, multiSortMeta, pageNumber, pageSize]);

  const getSchedulesCallback = useCallback(async () => {
    try {
      const result = await ScheduleService.getSchedules(filtersForApi);
      if (result.data) {
        setTotalCount((result.data.total === 0 ? 1 : result.data.total) ?? 1);
        setSchedules(result.data?.itemsList ?? []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось загрузить расписания',
        life: 3000
      });
      setLoading(false);
    }
  }, [filtersForApi]);

  useEffect(() => {
    getSchedulesCallback();
  }, [getSchedulesCallback]);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setPageNumber(1);
    setFirst(0);
    setFilters(_filters);
  };

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    setPageNumber(1);
    setFirst(0);

    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const addButtonHandle = () => {
    setSelectedSchedule(ScheduleTemplate);
    setDialogVisible(true);
  };

  const handleDeleteConfirmation = () => {
    handleDelete(scheduleToDelete);
    setDeleteDialogVisible(false);
  };

  const handleUpdate = async (
    id: number,
    updatedSchedule: ScheduleWithSettings
  ) => {
    const result = await ScheduleService.updateSchedule(id, updatedSchedule);
    getSchedulesCallback();
    return result;
  };

  const handleDelete = async (id: string | number | null) => {
    if (id) {
      await ScheduleService.deleteSchedule(parseInt(id.toString()));
      getSchedulesCallback();
    }
  };

  const handleAdd = async (schedules: ScheduleWithSettings) => {
    const result = await ScheduleService.createSchedule(schedules);
    getSchedulesCallback();
    return result;
  };

  const closeDialog = () => {
    // Function to close the dialog in parent
    setDialogVisible(false);
  };

  const onHideScheduleCard = () => {
    if (scheduleCardRef.current !== null) {
      scheduleCardRef.current.handleCancel();
    }
  };
  const editButtonHandle = () => {
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите расписание для редактирования',
        life: 3000
      });
    }
    if (isSelectedIdExist) setDialogVisible(true);
  }

  const deleteButtonHandle = () => {
    // If a valid process is selected, render the deletion confirmation dialog.
    if (isSelectedIdExist) {
      setScheduleToDelete(selectedSchedule.id);
      setDeleteDialogVisible(true);
    }
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите расписание для удаления',
        life: 3000
      });
    }
  }

  const header = (  
    <TablePageHeader
      tableName="schedules"
      onGlobalSearchChange={value =>
        handleInputChange('global')({ target: { value } } as React.ChangeEvent<HTMLInputElement>)
      }
      onAdd={addButtonHandle}
      onEdit={editButtonHandle}
      onDelete={deleteButtonHandle}
    />
  )

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement]);

  return (
    <div className="schedules-table wrapper wrapper--main">
      <Toast ref={toast} />

      <Dialog
        header="Карточка расписания"
        visible={dialogVisible}
        style={{
          minWidth: '1200px',
          width: '1200px',
          minHeight: 'fit-content'
        }}
        maximizable
        modal
        onHide={() => onHideScheduleCard()}
      >
        <VisibilityWrapper code="schedule-card__roles">
          <ScheduleCard
            scheduleProps={selectedSchedule}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onAdd={handleAdd}
            closeDialog={closeDialog}
            ref={scheduleCardRef}
          />
        </VisibilityWrapper>
      </Dialog>

      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={handleDeleteConfirmation}
        entityType="расписание"
        entityName={selectedSchedule?.name}
      />
      <DataTable
        filters={filters}
        paginator
        first={first}
        rows={pageSize}
        onPage={onPageChanged}
        totalRecords={totalCount}
        paginatorLeft={' '}
        paginatorRight={`Всего: ${schedules.length > 0 ? totalCount : 0}`}
        rowsPerPageOptions={[10, 25, 50, 100]}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink JumpToPageInput"
        currentPageReportTemplate="{currentPage} из {totalPages}"
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedSchedule!}
        onSelectionChange={(e) =>
          setSelectedSchedule(e.value as ScheduleWithSettings)
        }
        onRowDoubleClick={(e) => {
          setDialogVisible(true);
          setSelectedSchedule(e.data as ScheduleWithSettings);
        }}
        metaKeySelection={false}
        scrollable
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={schedules}
        dataKey="id"
        filterDisplay="row"
        loading={loading}
        header={header}
        emptyMessage="Расписания не найдены."
        onRowReorder={(e) => setSchedules(e.value)}
        tableStyle={{ minWidth: '50rem' }}
        onFilter={onFiltersChange}
      >
        <Column
          columnKey="name"
          sortable
          field="name"
          header="Название"
          filter
          onFilterClear={() =>
            clearFilter(
              'name',
              filters,
              setFilters,
              nameFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={nameFilterRef}
                onChange={handleInputChange('name')}
                placeholder="Поиск по названию работ"
              />
            </div>
          }
          style={{ width: '40%' }}
        />
        <Column
          columnKey="description"
          sortable
          field="description"
          header="Описание"
          sortField="description"
          filter
          onFilterClear={() =>
            clearFilter(
              'description',
              filters,
              setFilters,
              descriptionFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={descriptionFilterRef}
                onChange={handleInputChange('description')}
                placeholder="Поиск по описанию"
              />
            </div>
          }
          style={{ width: '60%' }}
        />
      </DataTable>
    </div>
  );
}
